import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "./components/ui/sonner";
import { AuthProvider, useAuth } from "./contexts/AuthContext";

// Pages
import Login from "./pages/Login";
import Register from "./pages/Register";
import Dashboard from "./pages/Dashboard";
import Home from "./pages/Home";
import StoryGenerator from "./pages/StoryGenerator";
import Ideas from "./pages/Ideas";
import ProductionPacks from "./pages/ProductionPacks";
import ProductionPackDetail from "./pages/ProductionPackDetail";
import ContentCalendar from "./pages/ContentCalendar";
import BrandLibrary from "./pages/BrandLibrary";
import TodoList from "./pages/TodoList";
import TeamMembers from "./pages/TeamMembers";
import ImportantNotes from "./pages/ImportantNotes";
import CreatorOutreach from "./pages/CreatorOutreach";
import CreatorPackDetail from "./pages/CreatorPackDetail";
import CustomIPList from "./pages/CustomIPList";
import CustomIPDetail from "./pages/CustomIPDetail";
import CelebrityIPList from "./pages/CelebrityIPList";
import CelebrityIPDetail from "./pages/CelebrityIPDetail";
import RedditLeadGen from "./pages/RedditLeadGen";
import Contacts from "./pages/Contacts";
import SendQueue from "./pages/SendQueue";
import Platforms from "./pages/Platforms";
import AdsHub from "./pages/AdsHub";
import AdsCreativeDetail from "./pages/AdsCreativeDetail";
import Research from "./pages/Research";
import InfluencerDetail from "./pages/InfluencerDetail";
import E2EClonerDetail from "./pages/E2EClonerDetail";
import RedditPosts from "./pages/RedditPosts";
import RedditPostDetail from "./pages/RedditPostDetail";
import CaptionGenerator from "./pages/CaptionGenerator";
import DescriptionGenerator from "./pages/DescriptionGenerator";
import TitleGenerator from "./pages/TitleGenerator";
import MediaGenerator from "./pages/MediaGenerator";
import Etsy from "./pages/Etsy";
import EtsyProductDetail from "./pages/EtsyProductDetail";
import AdCreatives from "./pages/AdCreatives";
import StorySets from "./pages/StorySets";
import StorySetDetail from "./pages/StorySetDetail";
import Developers from './pages/Developers';
import AdCreativeSetDetail from "./pages/AdCreativeSetDetail";
import Pitch from "./pages/Pitch";
import MoodBoardGenerator from "./pages/MoodBoardGenerator";
import RenderGenerator from "./pages/RenderGenerator";
import OliviaPage from "./pages/OliviaPage";
import VideoBank from "./pages/VideoBank";
import Incentives from "./pages/Incentives";

// Layout
import MainLayout from "./layouts/MainLayout";

// Protected Route wrapper
const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FAF7F2]">
        <div className="animate-pulse text-[#9A8E85]">Loading… ✨</div>
      </div>
    );
  }
  
  if (!user) {
    return <Navigate to="/login" replace />;
  }
  
  return children;
};

// Home redirect based on role
const HomeRedirect = () => {
  const { user } = useAuth();
  if (user?.role === 'editor') return <Navigate to="/production-packs" replace />;
  return <Home />;
};

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Routes>
          {/* Public Routes */}
          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<Register />} />
          <Route path="/pitch" element={<Pitch />} />
          
          {/* Protected Routes */}
          <Route path="/" element={
            <ProtectedRoute>
              <MainLayout />
            </ProtectedRoute>
          }>
            <Route index element={<HomeRedirect />} />
            <Route path="story-generator" element={<StoryGenerator />} />
            <Route path="caption-generator" element={<CaptionGenerator />} />
            <Route path="description-generator" element={<DescriptionGenerator />} />
            <Route path="title-generator" element={<TitleGenerator />} />
            <Route path="mood-board" element={<MoodBoardGenerator />} />
            <Route path="render-3d" element={<RenderGenerator />} />
            <Route path="olivia" element={<OliviaPage />} />
            <Route path="media-generator" element={<MediaGenerator />} />
            <Route path="etsy" element={<Etsy />} />
            <Route path="etsy/:id" element={<EtsyProductDetail />} />
            <Route path="ad-creatives" element={<AdCreatives />} />
            <Route path="ad-creatives/:id" element={<AdCreativeSetDetail />} />
            <Route path="story-sets" element={<StorySets />} />
            <Route path="story-sets/:id" element={<StorySetDetail />} />
            <Route path="video-bank" element={<VideoBank />} />
            <Route path="incentives" element={<Incentives />} />
            <Route path="ideas" element={<Navigate to="/ideas/content" replace />} />
            <Route path="ideas/content" element={<Ideas ideaType="content" />} />
            <Route path="ideas/custom-ip" element={<CustomIPList />} />
            <Route path="custom-ip/:id" element={<CustomIPDetail />} />
            <Route path="ideas/celebrity-ip" element={<CelebrityIPList />} />
            <Route path="celebrity-ip/:id" element={<CelebrityIPDetail />} />
            <Route path="ideas/reddit" element={<RedditPosts />} />
            <Route path="reddit-posts/:id" element={<RedditPostDetail />} />
            <Route path="reddit-leads" element={<RedditLeadGen />} />
            <Route path="production-packs" element={<ProductionPacks />} />
            <Route path="production-packs/:id" element={<ProductionPackDetail />} />
            <Route path="calendar" element={<ContentCalendar />} />
            <Route path="brand-library" element={<BrandLibrary />} />
            <Route path="todo" element={<TodoList />} />
            <Route path="team" element={<TeamMembers />} />
            <Route path="notes" element={<ImportantNotes />} />
            <Route path="creator-outreach" element={<CreatorOutreach />} />
            <Route path="creator-outreach/:id" element={<CreatorPackDetail />} />
            <Route path="contacts" element={<Contacts />} />
            <Route path="send-queue" element={<SendQueue />} />
            <Route path="platforms" element={<Platforms />} />
            <Route path="ads" element={<AdsHub />} />
            <Route path="ads/creatives/:id" element={<AdsCreativeDetail />} />
            <Route path="research" element={<Research />} />
            <Route path="research/influencers/:id" element={<InfluencerDetail />} />
            <Route path="research/e2e-cloner/:id" element={<E2EClonerDetail />} />
            <Route path="developers" element={<Developers />} />
          </Route>
          
          {/* Fallback */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
        <Toaster position="top-right" />
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
